import DashboardLayout from '@/components/DashboardLayout'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { router } from '@inertiajs/react'
import { 
  ArrowLeft, 
  CheckCircle, 
  XCircle, 
  Clock, 
  Send,
  RefreshCw,
  Search,
  AlertCircle,
  User
} from 'lucide-react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '@/components/ui/alert'
import { useState, useMemo } from 'react'

interface Notification {
  id: number
  title: string
  body: string
  status: string
  audience_type: string | null
  sender_name: string
  recipients_count: number
  sent_count: number
  failed_count: number
  success_rate: number
  sent_at: string
  created_at: string
}

interface Recipient {
  id: number
  user_id: number
  user_name: string
  user_email: string
  user_phone: string | null
  status: string
  error_message: string | null
  sent_at: string | null
  delivered_at: string | null
}

interface Props {
  notification: Notification
  recipients: Recipient[]
}

export default function PushNotificationsShow({ notification, recipients }: Props) {
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [isResending, setIsResending] = useState(false)

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'sent':
      case 'delivered':
        return (
          <Badge className="bg-green-500">
            <CheckCircle className="w-3 h-3 mr-1" />
            {status === 'delivered' ? 'Entregue' : 'Enviado'}
          </Badge>
        )
      case 'failed':
        return (
          <Badge variant="destructive">
            <XCircle className="w-3 h-3 mr-1" />
            Falhou
          </Badge>
        )
      case 'pending':
        return (
          <Badge variant="secondary">
            <Clock className="w-3 h-3 mr-1" />
            Pendente
          </Badge>
        )
      default:
        return <Badge>{status}</Badge>
    }
  }

  const filteredRecipients = useMemo(() => {
    return recipients.filter(recipient => {
      // Busca por nome ou email
      const matchesSearch = recipient.user_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           recipient.user_email.toLowerCase().includes(searchQuery.toLowerCase())
      
      // Filtro de status
      let matchesStatus = true
      if (statusFilter !== 'all') {
        matchesStatus = recipient.status === statusFilter
      }

      return matchesSearch && matchesStatus
    })
  }, [recipients, searchQuery, statusFilter])

  const handleResend = () => {
    if (!confirm(`Deseja reenviar para ${notification.failed_count} destinatário(s) com falha?`)) {
      return
    }

    setIsResending(true)
    router.post(`/push_notifications/${notification.id}/resend`, {}, {
      onFinish: () => {
        setIsResending(false)
      }
    })
  }

  const failedRecipients = recipients.filter(r => r.status === 'failed')
  const sentRecipients = recipients.filter(r => r.status === 'sent' || r.status === 'delivered')
  const pendingRecipients = recipients.filter(r => r.status === 'pending')

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <Button
              variant="ghost"
              onClick={() => router.visit('/push_notifications')}
              className="mb-2"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Voltar
            </Button>
            <h1 className="text-3xl font-bold tracking-tight">Detalhes da Notificação</h1>
            <p className="text-muted-foreground">
              Visualize os destinatários e status de entrega
            </p>
          </div>
          
          {notification.failed_count > 0 && (
            <Button
              size="lg"
              onClick={handleResend}
              disabled={isResending}
            >
              {isResending ? (
                <>
                  <Clock className="mr-2 h-4 w-4 animate-spin" />
                  Reenviando...
                </>
              ) : (
                <>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Reenviar Falhas ({notification.failed_count})
                </>
              )}
            </Button>
          )}
        </div>

        {/* Resumo da Notificação */}
        <Card>
          <CardHeader>
            <CardTitle>{notification.title}</CardTitle>
            <CardDescription>{notification.body}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Enviado por</p>
                <p className="font-medium">{notification.sender_name}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Data de Envio</p>
                <p className="font-medium">{notification.sent_at || notification.created_at}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Público</p>
                <p className="font-medium">{notification.audience_type || 'Personalizado'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Taxa de Sucesso</p>
                <p className="font-medium text-green-600">{notification.success_rate}%</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Estatísticas */}
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total</CardTitle>
              <User className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{notification.recipients_count}</div>
              <p className="text-xs text-muted-foreground">Destinatários</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Enviadas</CardTitle>
              <CheckCircle className="h-4 w-4 text-green-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">{notification.sent_count}</div>
              <p className="text-xs text-muted-foreground">
                {((notification.sent_count / notification.recipients_count) * 100).toFixed(1)}% do total
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Falhas</CardTitle>
              <XCircle className="h-4 w-4 text-red-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">{notification.failed_count}</div>
              <p className="text-xs text-muted-foreground">
                {((notification.failed_count / notification.recipients_count) * 100).toFixed(1)}% do total
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Pendentes</CardTitle>
              <Clock className="h-4 w-4 text-amber-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-amber-600">
                {notification.recipients_count - notification.sent_count - notification.failed_count}
              </div>
              <p className="text-xs text-muted-foreground">Aguardando</p>
            </CardContent>
          </Card>
        </div>

        {/* Alerta de Falhas */}
        {notification.failed_count > 0 && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Algumas notificações falharam</AlertTitle>
            <AlertDescription>
              {notification.failed_count} destinatário(s) não receberam a notificação. 
              Você pode tentar reenviar clicando no botão acima.
            </AlertDescription>
          </Alert>
        )}

        {/* Lista de Destinatários */}
        <Card>
          <CardHeader>
            <CardTitle>Destinatários ({filteredRecipients.length})</CardTitle>
            <CardDescription>
              Lista completa de todos os destinatários e status de entrega
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Filtros */}
            <div className="flex gap-3 mb-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar por nome ou e-mail..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos ({recipients.length})</SelectItem>
                  <SelectItem value="sent">Enviados ({sentRecipients.length})</SelectItem>
                  <SelectItem value="failed">Falhas ({failedRecipients.length})</SelectItem>
                  <SelectItem value="pending">Pendentes ({pendingRecipients.length})</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Tabela */}
            <div className="border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Destinatário</TableHead>
                    <TableHead>Contato</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Data/Hora</TableHead>
                    <TableHead>Erro</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredRecipients.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                        Nenhum destinatário encontrado
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredRecipients.map((recipient) => (
                      <TableRow key={recipient.id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{recipient.user_name}</p>
                            <p className="text-sm text-muted-foreground">{recipient.user_email}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <p className="text-sm">{recipient.user_phone || '-'}</p>
                        </TableCell>
                        <TableCell>
                          {getStatusBadge(recipient.status)}
                        </TableCell>
                        <TableCell>
                          <p className="text-sm">
                            {recipient.delivered_at || recipient.sent_at || '-'}
                          </p>
                        </TableCell>
                        <TableCell>
                          {recipient.error_message ? (
                            <p className="text-sm text-red-600 max-w-xs truncate" title={recipient.error_message}>
                              {recipient.error_message}
                            </p>
                          ) : (
                            <p className="text-sm text-muted-foreground">-</p>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  )
}
